// src/managers/FileAttachmentManager/FileAttachmentManager.jsx
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { authManager } from '@/managers/AuthManager';
import { getApiBaseUrl } from '@/utils/setProdOrDev'; // Import the new utility

const CONFIG = {
    IMAGE: {
        FORMATS: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
        MAX_SIZE: 10 * 1024 * 1024,
        MAX_DIMENSION: 2048
    },
    TEXT: { MAX_SIZE: 5 * 1024 * 1024 },
    PDF: { MAX_SIZE: 30 * 1024 * 1024 },
    AUDIO: {
        FORMATS: ['audio/webm', 'audio/mp4', 'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/aac'],
        MAX_SIZE: 25 * 1024 * 1024
    }
};

const generateUniqueFileId = () => `file_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

const TEXT_EXTENSIONS = [
    'js', 'jsx', 'ts', 'tsx', 'py', 'rb', 'java', 'c', 'cpp', 'cs', 'go', 'rs', 'php', 'swift', 
    'kt', 'scala', 'groovy', 'pl', 'pm', 'sh', 'bash', 'ps1', 'lua', 'r', 'dart', 'sql', 'asm',
    'vb', 'f', 'f90', 'hs', 'clj', 'erl', 'ex', 'elm', 'lisp', 'scm', 'rkt', 'ml',
    'html', 'htm', 'xhtml', 'css', 'scss', 'sass', 'less', 'xml', 'vue', 'svelte',
    'json', 'yaml', 'yml', 'toml', 'ini', 'conf', 'properties', 'env', 'csv', 'tsv',
    'md', 'markdown', 'txt', 'rtf', 'tex', 'rst', 'adoc', 'wiki',
    'log', 'diff', 'patch', 'gitignore', 'dockerignore', 'editorconfig', 'Dockerfile',
];

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'tif', 'ico', 'svg' ,'heic', 'heif'];

/* ========================
 * isImageFile - Exportable helper to detect image files
 * ========================*/
export const isImageFile = (file) => {
    if (!file) return false;
    const type = file.type || file.mimeType || '';
    if (type.startsWith('image/')) {
        return true;
    }
    const extension = file.name ? file.name.split('.').pop().toLowerCase() : '';
    return IMAGE_EXTENSIONS.includes(extension);
};

/* ========================
 * isTextFile - Exportable helper to detect text-based files
 * ========================*/
export const isTextFile = (file) => {
    const extension = file.name ? file.name.split('.').pop().toLowerCase() : '';
    if (extension === 'svg') return false; // SVGs are images first.

    return file.type.startsWith('text/') || 
           file.type.includes('javascript') || 
           file.type.includes('json') || 
           file.type.includes('xml') ||
           file.type.includes('html') ||
           file.type.includes('css') ||
           TEXT_EXTENSIONS.includes(extension) ||
           file.name === 'Pasted Clipboard.txt';
};

const processTextFile = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result;
            const lines = content.split(/\r\n|\r|\n/);
            const contentWithLines = lines.map((line, index) => `<LINE:${index + 1}>${line}`).join('\n');
            
            resolve({
                content,
                contentWithLines,
                size: new Blob([content]).size,
                lineCount: lines.length,
                preview: content.substring(0, 200)
            });
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
    });
};

const generateImagePreview = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to generate preview'));
        reader.readAsDataURL(file);
    });
};

const validateFile = (file) => {
    if (!file) throw new Error('No file provided');
    if (file.size > CONFIG.PDF.MAX_SIZE) throw new Error(`File exceeds max size of ${CONFIG.PDF.MAX_SIZE / 1024 / 1024}MB`);
    return true; // Simplified for robustness, specific checks can be added back if needed.
};

// ==============================================================================
// === uploadToFirebase - Calls the secure, server-side proxy endpoint
// ==============================================================================
const uploadToFirebase = async (file, userId, compositionId) => {
    if (!userId) throw new Error('Authentication required');
    if (!compositionId) throw new Error('Composition ID required for upload.');

    const user = authManager.getCurrentUser();
    if (!user) throw new Error('Authentication required: User not found.');

    const token = await user.getIdToken();
    if (!token) throw new Error('Authentication token could not be retrieved.');

    const formData = new FormData();
    formData.append('image', file); // 'image' must match the field name in multer.single('image') on the server
    formData.append('userId', userId);
    formData.append('compositionId', compositionId);

    try {
        // Use the centralized API base URL
        const apiBaseUrl = getApiBaseUrl(); 
        const response = await fetch(`${apiBaseUrl}/upload-transient-image`, { 
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            // Attempt to parse JSON, but fall back to text if that fails.
            let errorData;
            try {
                errorData = await response.json();
            } catch (e) {
                errorData = { message: await response.text() };
            }
            throw new Error(errorData.message || 'Server failed to upload image.');
        }

        const result = await response.json();
        return result.url; // The server will return the download URL
    } catch (error) {
        console.error(`Client-side upload to proxy failed:`, error);
        throw error;
    }
};
// ==============================================================================


export const FileAttachmentContext = createContext();

export const useFileAttachment = () => {
    const context = useContext(FileAttachmentContext);
    if (!context) {
        throw new Error('useFileAttachment must be used within a FileAttachmentProvider');
    }
    return context;
};

export const FileAttachmentProvider = ({ children }) => {
    const [stagedFiles, setStagedFiles] = useState({});
    const [authState, setAuthState] = useState(() => authManager.getState());

    useEffect(() => {
        const unsubscribe = authManager.subscribe(setAuthState);
        return () => unsubscribe();
    }, []);

    const updateFile = useCallback((fileId, updates) => {
        setStagedFiles(prev => {
            const fileExists = prev[fileId];
            // BUG FIX: Correctly spreads previous state before updating the target file.
            return fileExists ? { ...prev, [fileId]: { ...prev[fileId], ...updates } } : prev;
        });
    }, []);
    
    /* ========================
     * addFiles - NEW: Processes files and triggers uploads immediately.
     * Provides progressive UI updates from "processing" to "uploading" to "ready".
     * ========================*/
    const addFiles = useCallback(async (files, isPasted = false) => {
        const compositionId = `comp_batch_${Date.now()}`;

        const processingPromises = files.map(async (file) => {
            const fileId = generateUniqueFileId();

            // Step 1: Immediately stage the file with a 'processing' status for instant UI feedback.
            const initialFileState = {
                id: fileId, name: file.name, type: file.type,
                size: file.size, isPasted, status: 'processing', fileBlob: file,
                isEditable: isPasted && isTextFile(file) && !isImageFile(file),
            };
            setStagedFiles(prev => ({ ...prev, [fileId]: initialFileState }));

            try {
                validateFile(file);
                const isImg = isImageFile(file);
                const isTxt = !isImg && isTextFile(file);

                if (isImg) {
                    const preview = await generateImagePreview(file);
                    updateFile(fileId, { preview, status: 'uploading' });

                    const storageUrl = await uploadToFirebase(file, authState.user?.uid, compositionId);
                    updateFile(fileId, { storageUrl, status: 'ready' });

                } else if (isTxt) {
                    const textData = await processTextFile(file);
                    updateFile(fileId, { ...textData, status: 'ready' });

                } else {
                    updateFile(fileId, { status: 'ready' });
                }
            } catch (error) {
                console.error(`Failed to process or upload file ${file.name}:`, error);
                updateFile(fileId, { status: 'error', error: error.message });
            }
        });

        await Promise.all(processingPromises);
    }, [authState.user?.uid, updateFile]);

    /* ========================
     * addFile - Kept for single-file additions (e.g., voice note)
     * It now uses the robust batch `addFiles` method internally.
     * ========================*/
    const addFile = useCallback(async (fileOrBlob, fileName, fileType, isPasted = false) => {
        const file = fileOrBlob instanceof File ? fileOrBlob : 
                   new File([fileOrBlob], fileName || 'untitled', { type: fileType || 'application/octet-stream' });
        await addFiles([file], isPasted);
    }, [addFiles]);

    const removeFile = useCallback((fileId) => {
        setStagedFiles(prev => {
            const { [fileId]: removed, ...rest } = prev;
            if (removed?.preview && removed.preview.startsWith('blob:')) {
                URL.revokeObjectURL(removed.preview);
            }
            return rest;
        });
    }, []);

    const clearFiles = useCallback(() => {
        Object.values(stagedFiles).forEach(file => {
            if (file.preview && file.preview.startsWith('blob:')) {
                URL.revokeObjectURL(file.preview);
            }
        });
        setStagedFiles({});
    }, [stagedFiles]);

    const hasUnreadyFiles = useMemo(() => {
        return Object.values(stagedFiles).some(file => file.status !== 'ready' && file.status !== 'error');
    }, [stagedFiles]);

    /* ========================
     * uploadFiles - DEPRECATED FOR UPLOADING.
     * Now confirms which files are ready to be sent with the message.
     * ========================*/
    const uploadFiles = useCallback(async (files) => {
        return Object.values(files).filter(file => file.status === 'ready' && file.storageUrl);
    }, []);
    
    const stageExistingFiles = useCallback((filesToStage) => {
        Object.values(stagedFiles).forEach(file => {
            if (file.preview && file.preview.startsWith('blob:')) {
                URL.revokeObjectURL(file.preview);
            }
        });
        setStagedFiles(filesToStage);
    }, [stagedFiles]);

    const value = useMemo(() => ({
        stagedFiles,
        addFile,
        addFiles,
        removeFile,
        updateFile,
        clearFiles,
        hasUnreadyFiles,
        uploadFiles,
        stageExistingFiles,
        isAuthenticated: !!authState.user
    }), [
        stagedFiles,
        addFile,
        addFiles,
        removeFile,
        updateFile,
        clearFiles,
        hasUnreadyFiles,
        uploadFiles,
        stageExistingFiles,
        authState.user
    ]);

    return (
        <FileAttachmentContext.Provider value={value}>
            {children}
        </FileAttachmentContext.Provider>
    );
};

export default FileAttachmentProvider;